import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import { formatNumberCH, sanitizeKey } from "../../../../lib/utils/format";
import {
  createScales,
  createYScale,
  drawGrid,
  drawAxes,
  createTooltipHandler,
} from "../../../../lib/d3/chartCore";

export interface UnifiedD3ChartProps {
  data?: any[];
  categories?: string[];
  allCategories?: string[] | null;
  mode?: string; // "line" | "column"
  isVolume?: boolean;
  showTotal?: boolean;
  width?: number;
  height?: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  isExpanded?: boolean;
  previewHideFinalYear?: boolean;
  colorOf?: (c: string) => string;
  metricSuffix?: string;
  yTickCount?: number | null;
  yTickValues?: number[] | null;
  children?: React.ReactNode;
}

const UnifiedD3Chart: React.FC<UnifiedD3ChartProps> = ({
  data = [],
  categories = [],
  allCategories = null,
  mode = "line",
  isVolume = false,
  showTotal = false,
  width = 800,
  height = 380,
  margin = { top: 20, right: 30, bottom: 60, left: 60 },
  isExpanded = false,
  colorOf = () => "#333",
  metricSuffix = "__volume",
  yTickCount = null,
  yTickValues = null,
  children,
  previewHideFinalYear = false,
}) => {
  // Component note:
  // - Orchestrates scales, axes and drawing helpers from `chartCore` to render
  //   either line or stacked column charts depending on `mode`.
  // - `metricSuffix` controls which fields are read from the data (e.g. "__volume").
  // - `previewHideFinalYear` is a UX heuristic: if the latest year contains only H1 data
  //   the preview (non-expanded) view will omit it to avoid misleading full-year totals.
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!data?.length || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const svgRect = svgRef.current.getBoundingClientRect();
    const renderedWidth = svgRect?.width || width;

    let displayData = data;
    if (!isExpanded && previewHideFinalYear && Array.isArray(data) && data.length > 0) {
      displayData = data.slice(0, data.length - 1);
    }

    const { chartWidth, chartHeight, xScale } = createScales(
      displayData,
      renderedWidth,
      height,
      margin
    );

    svg
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", renderedWidth)
      .attr("height", height)
      .attr("fill", "transparent");

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const maxValue = calculateMaxValue(
      displayData,
      categories,
      mode,
      showTotal,
      metricSuffix,
      allCategories,
      isVolume
    );
    const yScale = createYScale(maxValue, chartHeight);

    drawGrid(g, chartWidth, chartHeight, xScale, yScale, yTickValues);
    drawAxes(g, chartWidth, chartHeight, xScale, yScale, isVolume, yTickValues, isExpanded);

    const tooltip = d3.select(tooltipRef.current);
    const tooltipHandler = createTooltipHandler(tooltip, margin, chartWidth);

    if (mode === "column") {
      drawStackedBars(
        g,
        displayData,
        categories,
        xScale,
        yScale,
        colorOf,
        metricSuffix,
        tooltipHandler,
        margin,
        isVolume
      );
    } else {
      drawLineChart(
        g,
        displayData,
        categories,
        xScale,
        yScale,
        colorOf,
        metricSuffix,
        tooltipHandler,
        margin,
        isVolume
      );
    }

    if (
      showTotal &&
      displayData.some(
        (d) => d.totalVolume || d.totalCount || d.__grandTotalVolume || d.__grandTotalCount
      )
    ) {
      drawTotalLine(
        g,
        displayData,
        xScale,
        yScale,
        isVolume,
        metricSuffix,
        allCategories || categories,
        tooltipHandler,
        margin
      );
    }
  }, [
    data,
    categories,
    mode,
    isVolume,
    showTotal,
    width,
    height,
    margin,
    isExpanded,
    previewHideFinalYear,
    colorOf,
    metricSuffix,
    yTickCount,
    yTickValues,
    allCategories,
  ]);

  return (
    <div style={{ position: "relative" }}>
      {children}
      <svg
        ref={svgRef}
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
      />
      <div
        ref={tooltipRef}
        style={{
          position: "absolute",
          pointerEvents: "none",
          opacity: 0,
          transition: "opacity 0.2s",
          zIndex: 50,
        }}
      />
    </div>
  );
};

function calculateMaxValue(
  data: any[],
  categories: string[],
  mode: string,
  showTotal: boolean,
  suffix: string,
  allCategories: string[] | null,
  isVolume: boolean
) {
  // calculateMaxValue:
  // - Determines the numeric maximum used for Y-axis scaling.
  // - For `column` mode it computes stacked sums per year; for `line` mode it
  //   inspects category-specific fields. When `showTotal` is true we also
  //   consider explicit total fields (`totalVolume`, `__grandTotalVolume`, etc.)
  //   so the axis can accommodate an overlaid total line.
  if (mode === "column") {
    const stack = d3.stack().keys(categories.map((cat) => sanitizeKey(cat)));
    const stackedData = stack(
      data.map((d) => {
        const row: Record<string, number | string> = { year: d.year } as any;
        categories.forEach((cat) => {
          row[sanitizeKey(cat)] = d[`${sanitizeKey(cat)}${suffix}`] || 0;
        });
        return row as any;
      })
    );
    const maxStack = d3.max(stackedData, (series: any) => d3.max(series, (d: any) => d[1])) as
      | number
      | undefined;

    if (!showTotal) return maxStack || 0;

    const totalFieldMax = d3.max(data, (d: any) => {
      const pre = isVolume
        ? (d.__grandTotalVolume ?? d.totalVolume)
        : (d.__grandTotalCount ?? d.totalCount);
      if (pre != null) return pre as number;
      const metricKeys = Object.keys(d).filter((k) => k.endsWith(suffix));
      if (metricKeys.length) return d3.sum(metricKeys.map((k) => (d[k] || 0) as number));
      return 0;
    }) as number | undefined;
    return Math.max(maxStack || 0, totalFieldMax || 0);
  }

  const maxCategory = d3.max(data, (d: any) =>
    d3.max(categories, (cat) => (d[`${sanitizeKey(cat)}${suffix}`] || 0) as number)
  ) as number | undefined;

  if (!showTotal) return maxCategory || 0;

  const totalFieldMax = d3.max(data, (d: any) => {
    const pre = isVolume
      ? (d.__grandTotalVolume ?? d.totalVolume)
      : (d.__grandTotalCount ?? d.totalCount);
    if (pre != null) return pre as number;
    const metricKeys = Object.keys(d).filter((k) => k.endsWith(suffix));
    if (metricKeys.length) return d3.sum(metricKeys.map((k) => (d[k] || 0) as number));
    return 0;
  }) as number | undefined;

  return Math.max(maxCategory || 0, totalFieldMax || 0);
}

function drawStackedBars(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  data: any[],
  categories: string[],
  xScale: any,
  yScale: any,
  colorOf: (c: string) => string,
  suffix: string,
  tooltipHandler: { show: (x: number, y: number, html: string) => void; hide: () => void },
  margin: { top: number; right: number; bottom: number; left: number },
  isVolume: boolean
) {
  // drawStackedBars: render stacked rects grouped by category
  // - Builds a stacked layout with d3.stack and maps sanitized keys to rects.
  // - Hover computes the segment value and shows a tooltip positioned using
  //   chart margins so it aligns with the SVG coordinate system.
  const stack = d3.stack().keys(categories.map((cat) => sanitizeKey(cat)));
  const stackedData = stack(
    data.map((d) => {
      const row: Record<string, number | string> = { year: d.year } as any;
      categories.forEach((cat) => {
        row[sanitizeKey(cat)] = d[`${sanitizeKey(cat)}${suffix}`] || 0;
      });
      return row as any;
    })
  );

  g.selectAll(".category-group")
    .data(stackedData)
    .enter()
    .append("g")
    .attr("class", "category-group")
    .attr("fill", (d, i) => colorOf(categories[i]))
    .selectAll("rect")
    .data((d) => d as any)
    .enter()
    .append("rect")
    .attr("x", (d: any) => xScale(d.data.year))
    .attr("y", (d: any) => yScale(d[1]))
    .attr("height", (d: any) => yScale(d[0]) - yScale(d[1]))
    .attr("width", xScale.bandwidth())
    .style("cursor", "pointer")
    .on("mouseover", function (event: any, d: any) {
      const categoryIndex = stackedData.findIndex((s: any) => s.includes(d));
      const category = categories[categoryIndex];
      const value = (d[1] as number) - (d[0] as number);
      const formattedValue = formatNumberCH(value, isVolume ? "auto" : 0);
      const xPos = margin.left + (xScale(d.data.year) + xScale.bandwidth() / 2);
      const yPos = margin.top + yScale(d[1]);
      tooltipHandler.show(
        xPos,
        yPos,
        `<strong>${category}</strong><br/>${d.data.year}: ${formattedValue}`
      );
    })
    .on("mouseout", () => tooltipHandler.hide());
}

function drawLineChart(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  data: any[],
  categories: string[],
  xScale: any,
  yScale: any,
  colorOf: (c: string) => string,
  suffix: string,
  tooltipHandler: { show: (x: number, y: number, html: string) => void; hide: () => void },
  margin: { top: number; right: number; bottom: number; left: number },
  isVolume: boolean
) {
  // drawLineChart: render multiple series as stroked paths with interactive dots
  // - Each category's metric values are read using the `suffix` (e.g. "__volume").
  // - `dot-hit-*` elements provide larger pointer targets while visible dots
  //   remain small for clarity.
  const line = d3
    .line<any>()
    .x((d) => xScale(d.year) + xScale.bandwidth() / 2)
    .y((d) => yScale(d.value))
    .curve(d3.curveMonotoneX);

  categories.forEach((category) => {
    const categoryKey = sanitizeKey(category);
    const lineData = data.map((d) => ({
      year: d.year,
      value: d[`${categoryKey}${suffix}`] || 0,
    }));

    g.append("path")
      .datum(lineData)
      .attr("fill", "none")
      .attr("stroke", colorOf(category))
      .attr("stroke-width", 2)
      .attr("d", line as any);

    g.selectAll(`.dot-${categoryKey}`)
      .data(lineData)
      .enter()
      .append("circle")
      .attr("class", `dot-${categoryKey}`)
      .attr("cx", (d) => xScale(d.year) + xScale.bandwidth() / 2)
      .attr("cy", (d) => yScale(d.value))
      .attr("r", 3)
      .attr("fill", colorOf(category))
      .style("cursor", "pointer")
      .on("mouseover", function (event: any, d: any) {
        const formattedValue = formatNumberCH(d.value, isVolume ? "auto" : 0);
        const xPos = margin.left + (xScale(d.year) + xScale.bandwidth() / 2);
        const yPos = margin.top + yScale(d.value);
        tooltipHandler.show(
          xPos,
          yPos,
          `<strong>${category}</strong><br/>${d.year}: ${formattedValue}`
        );
      })
      .on("mouseout", () => tooltipHandler.hide());

    g.selectAll(`.dot-hit-${categoryKey}`)
      .data(lineData)
      .enter()
      .append("circle")
      .attr("class", `dot-hit-${categoryKey}`)
      .attr("cx", (d) => xScale(d.year) + xScale.bandwidth() / 2)
      .attr("cy", (d) => yScale(d.value))
      .attr("r", 8)
      .attr("fill", "transparent")
      .style("pointer-events", "all")
      .style("cursor", "pointer")
      .on("mouseover", function (event: any, d: any) {
        const formattedValue = formatNumberCH(d.value, isVolume ? "auto" : 0);
        const xPos = margin.left + (xScale(d.year) + xScale.bandwidth() / 2);
        const yPos = margin.top + yScale(d.value);
        tooltipHandler.show(
          xPos,
          yPos,
          `<strong>${category}</strong><br/>${d.year}: ${formattedValue}`
        );
      })
      .on("mouseout", () => tooltipHandler.hide());
  });
}

function drawTotalLine(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  data: any[],
  xScale: any,
  yScale: any,
  isVolume: boolean,
  suffix: string,
  allCategories: string[] | null,
  tooltipHandler: { show: (x: number, y: number, html: string) => void; hide: () => void },
  margin: { top: number; right: number; bottom: number; left: number }
) {
  // drawTotalLine: compute and draw a dashed 'total' series
  // - `computeTotal` first prefers explicit total fields and otherwise sums
  //   sanitized category fields. The line is dashed and has its own hover
  //   handlers for tooltips.
  const computeTotal = (d: any) => {
    const pre = isVolume
      ? (d.__grandTotalVolume ?? d.totalVolume)
      : (d.__grandTotalCount ?? d.totalCount);
    if (pre != null) return pre as number;
    if (!allCategories?.length) return 0;
    return d3.sum(allCategories.map((cat) => d[`${sanitizeKey(cat)}${suffix}`] || 0));
  };

  const lineData = data
    .filter((d) => d.totalVolume || d.totalCount || d.__grandTotalVolume || d.__grandTotalCount)
    .map((d) => ({ year: d.year, value: computeTotal(d) }));

  const totalLine = d3
    .line<any>()
    .x((d) => xScale(d.year) + xScale.bandwidth() / 2)
    .y((d) => yScale(d.value))
    .curve(d3.curveMonotoneX);

  g.append("path")
    .datum(lineData)
    .attr("fill", "none")
    .attr("stroke", "#000")
    .attr("stroke-width", 3)
    .attr("stroke-dasharray", "5,5")
    .attr("d", totalLine as any);

  // draw small data points for the total line
  g.selectAll(".total-dot")
    .data(lineData)
    .enter()
    .append("circle")
    .attr("class", "total-dot")
    .attr("cx", (d: any) => xScale(d.year) + xScale.bandwidth() / 2)
    .attr("cy", (d: any) => yScale(d.value))
    .attr("r", 3)
    .attr("fill", "#000")
    .style("cursor", "pointer")
    .on("mouseover", function (event: any, d: any) {
      const formattedValue = formatNumberCH(d.value, isVolume ? "auto" : 0);
      const xPos = margin.left + (xScale(d.year) + xScale.bandwidth() / 2);
      const yPos = margin.top + yScale(d.value);
      tooltipHandler.show(xPos, yPos, `<strong>Total</strong><br/>${d.year}: ${formattedValue}`);
    })
    .on("mouseout", () => tooltipHandler.hide());

  g.selectAll(".total-dot-hit")
    .data(lineData)
    .enter()
    .append("circle")
    .attr("class", "total-dot-hit")
    .attr("cx", (d: any) => xScale(d.year) + xScale.bandwidth() / 2)
    .attr("cy", (d: any) => yScale(d.value))
    .attr("r", 8)
    .attr("fill", "transparent")
    .style("pointer-events", "all")
    .style("cursor", "pointer")
    .on("mouseover", function (event: any, d: any) {
      const formattedValue = formatNumberCH(d.value, isVolume ? "auto" : 0);
      const xPos = margin.left + (xScale(d.year) + xScale.bandwidth() / 2);
      const yPos = margin.top + yScale(d.value);
      tooltipHandler.show(xPos, yPos, `<strong>Total</strong><br/>${d.year}: ${formattedValue}`);
    })
    .on("mouseout", () => tooltipHandler.hide());
}

UnifiedD3Chart.displayName = "UnifiedD3Chart";

export default UnifiedD3Chart;
